import { useEffect, useRef, useState } from 'react';
import { useForm } from "react-hook-form"
import { Modal, ModalProps } from 'react-bootstrap';
import { useInduction } from '../../hooks/useInduction';
import { IFormInput, InductionQuestionConfiguration } from '../../TypesOrInterfaces/induction';
import { ALPHATEXT_PATTERN, DOSE_PATTERN } from '../../util/pattern';
import { caregiverApiAlias } from '../../util/aliasHidden';
import { Container, Conten, ModalContainer, ModalTitle } from './styles';
import Input from '../Form/Input';

interface QuizModalDynamicProps extends ModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    quizId: number;
}

const aliasHidden = [
    'question_accepted_term',
    'question_late-term_acceptance',
    'question_accepted_contact'
];

const prescriberStateAliases = [
    'question_prescriber_state',
    'question_prescriber_state_not_validation'
];

const prescriberCrmAliases = [
    'question_prescriber_crm',
    'question_prescriber_crm_not_validation'
];

const prescriberNameAliases = [
    'question_prescriber_name',
    'question_prescriber_name_not_validation'
];

export function QuizDynamicModal({ isOpen, onRequestClose, quizId }: QuizModalDynamicProps) {

    const { configuration
        , quizzes
        , benefitId
        , getPrescriber
        , prescriber
        , addQuiz
        , resetQuizzes
        , questionResponsibleDocument
        , questionResponsibleName
        , resetPrescriber
        , validateRepresentativeCode } = useInduction();

    const { register, handleSubmit, errors, clearErrors, setValue } = useForm();


    const [stateCod, setStateCode] = useState('');
    const [prescriberCrm, setPrescriberCrm] = useState('');

    const [codigoRepresentante, setCodigoRepresentante] = useState('');
    const [codigoRepresentanteValid, setCodigoRepresentanteValid] = useState<boolean | null>(null);
    const [codigoRepresentanteError, setCodigoRepresentanteError] = useState('');
    const [codigoRepresentanteValidating, setCodigoRepresentanteValidating] = useState(false);
    const representanteDebounceRef = useRef<NodeJS.Timeout | null>(null);

    //const fieldBirthday = configuration.fields.filter(f => f.apiAlias === 'beneficiario_dt_nascimento')[0].columnId;
    const quiz = configuration.quiz?.filter(quiz => quiz.id === quizId)[0];

    const response = quizzes.filter(f => f.id === quizId)[0]?.questions.filter(x => !!x);

    useEffect(() => {
        handleGetPrescribe(stateCod, prescriberCrm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateCod, prescriberCrm]);

    useEffect(() => {
        const code = codigoRepresentante.trim();

        if (representanteDebounceRef.current) {
            clearTimeout(representanteDebounceRef.current);
        }

        if (code.length === 0) {
            setCodigoRepresentanteValid(null);
            setCodigoRepresentanteError('');
            setCodigoRepresentanteValidating(false);
            return;
        }

        if (code.length < 5) {
            setCodigoRepresentanteValid(null);
            setCodigoRepresentanteError('');
            setCodigoRepresentanteValidating(false);
            return;
        }

        setCodigoRepresentanteValidating(true);
        representanteDebounceRef.current = setTimeout(async () => {
            const result = await validateRepresentativeCode(code);
            setCodigoRepresentanteValid(result.valid);
            setCodigoRepresentanteError(result.error);
            setCodigoRepresentanteValidating(false);
        }, 500);

        return () => {
            if (representanteDebounceRef.current) {
                clearTimeout(representanteDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codigoRepresentante]);

    function questionOrdering(a: InductionQuestionConfiguration, b: InductionQuestionConfiguration) {
        if (a.order > b.order)
            return 1;
        else if (a.order < b.order) return -1;
        return 0;
    }

    function handleSelectState(value: string) {
        setStateCode(value);
    }

    function handleSetPrescriberCrm(value: string) {
        setPrescriberCrm(value.replace(/\D/g, ''));
    }

    function handleRepresentanteChange(value: string) {
        const onlyDigits = value.replace(/\D/g, '').slice(0, 5);
        setCodigoRepresentante(onlyDigits);
    }

    async function handleGetPrescribe(stateCod: string, prescriberCRM: string) {
        if (stateCod && prescriberCRM)
            await getPrescriber(stateCod, prescriberCRM);
        else
            await resetPrescriber();

        return;
    }

    function handleCloseModal() {
        onRequestClose();
        resetQuizzes();
        setPrescriberCrm('');
        setStateCode('');
        setCodigoRepresentante('');
        setCodigoRepresentanteValid(null);
        setCodigoRepresentanteError('');
        setCodigoRepresentanteValidating(false);
    }

    function handleSubmitQuiz(data: IFormInput) {
        // Inject representative code value (default "0000" if empty)
        const representativeQuestion = quiz.questions.find(
            q => q.questionAlias.toLowerCase() === 'question_representative_number'
        );
        if (representativeQuestion) {
            data[String(representativeQuestion.id)] = codigoRepresentante.trim() || '0000';
        }

        addQuiz(data, quiz);
        handleCloseModal();
    }

    function handleKeyPressOnlyNumberAndDot(event: any) {
        const regex = new RegExp(/^[0-9,]*$/);
        const key = String.fromCharCode(!event.keyCode ? event.which : event.charCode);
        if (!regex.test(key)) {
          event.preventDefault();
          return false;
        }
    }

    return (
        <ModalContainer>
            <Modal
                show={isOpen}
                onHide={onRequestClose}
                animation={false}
                backdrop="static"
                keyboard={false}
                className='modalAdesao'
                size='xl'
            >
                <Conten>
                    <Modal.Header closeButton>
                        <div className="header-container">
                            <ModalTitle>{quiz?.name}</ModalTitle>
                        </div>
                    </Modal.Header>
                    <Container onSubmit={handleSubmit(handleSubmitQuiz)}>
                        <Modal.Body>
                            {quiz.questions.filter(x => !caregiverApiAlias.includes(x.questionAlias.toLocaleLowerCase())).sort(questionOrdering).map(question => {

                                switch (question.questionType.toLowerCase()) {
                                    case "checkbox":
                                        if (aliasHidden.includes(question.questionAlias.toLowerCase())) {
                                            return (
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        name={String(question.id)}
                                                        hidden
                                                        ref={register}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (question.questionAlias.toLowerCase() !== 'question_responsible_acceptance' && question.questionAlias.toLowerCase() !== 'question_accept_contact_types') {
                                            return (
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        name={String(question.id)}

                                                        ref={register({ required: question.isRequired })}
                                                    />
                                                    <p>{question.title}</p>
                                                </div>
                                            );
                                        }

                                        return null;

                                    case "lista":
                                        if (prescriberStateAliases.includes(question.questionAlias.toLowerCase())) {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <div style={{ height: 72 }}>
                                                        <select
                                                            name={String(question.id)}
                                                            onChange={event => handleSelectState(event.target.value)}
                                                            defaultValue={response?.filter(f => f.id === question.id)[0]?.value}
                                                            ref={register({
                                                                required: {
                                                                    value: question.isRequired,
                                                                    message: `${question.title} Obrigatório`
                                                                }
                                                            })}
                                                        >
                                                            <option value=''></option>
                                                            {question.allowedValues?.map(allowedValue => (
                                                                <option key={allowedValue.value} value={allowedValue.value}
                                                                >
                                                                    {allowedValue.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors[String(question.id)] && <span className="input-error-message">{errors[String(question.id)].message}</span>}
                                                    </div>
                                                </div>
                                            )
                                        }

                                        if (!(question.questionAlias.toLowerCase() === 'question_responsible_degree_of_kinship')) {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <select
                                                        name={String(question.id)}
                                                        ref={register({ required: question.isRequired })}
                                                        defaultValue={response?.filter(f => f.id === question.id)[0]?.value}
                                                    >
                                                        <option value=''></option>
                                                        {question.allowedValues?.map(allowedValue => (
                                                            <option key={allowedValue.value} value={allowedValue.value}
                                                            >
                                                                {allowedValue.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            );
                                        }

                                        return null;
                                    case "texto":
                                        if (prescriberCrmAliases.includes(question.questionAlias.toLowerCase())) {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {`${question.title} (Para médicos do RJ, não inserir o prefixo 52)`}
                                                    </label>
                                                    <Input
                                                        register={register}
                                                        errors={errors}
                                                        type="text"
                                                        key={question.questionAlias.toLowerCase()}
                                                        name={String(question.id)}
                                                        defaultValue={response?.filter(f => f.id === question.id)[0]?.value}
                                                        placeholder={question.title}
                                                        maxLength={question.maximumAnswerLength}
                                                        errorMessage={`${question.title} obrigatório`}
                                                        min={1}
                                                        required={question.isRequired}
                                                        onChange={(event: any) => handleSetPrescriberCrm(event.target.value)}
                                                    />
                                                </div>
                                            )
                                        }

                                        if (prescriberNameAliases.includes(question.questionAlias.toLowerCase())) {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name={String(question.id)}
                                                        placeholder={question.title}
                                                        value={prescriber?.name || response?.filter(f => f.id === question.id)[0]?.value || ''}
                                                        ref={register()}
                                                        disabled
                                                        readOnly
                                                    />
                                                </div>
                                            )
                                        }

                                        if (question.questionAlias.toLowerCase() === 'question_representative_number') {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        Código de representante
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name={String(question.id)}
                                                        placeholder="00000"
                                                        maxLength={5}
                                                        inputMode="numeric"
                                                        value={codigoRepresentante}
                                                        onChange={(e) => handleRepresentanteChange(e.target.value)}
                                                        ref={register()}
                                                        style={{
                                                            borderColor: codigoRepresentanteValid === false ? '#dc3545'
                                                                : codigoRepresentanteValid === true ? '#28a745'
                                                                    : undefined
                                                        }}
                                                    />
                                                    {codigoRepresentanteValidating && (
                                                        <span className="input-info-message" style={{ color: '#6c757d', fontSize: '0.85rem' }}>Validando...</span>
                                                    )}
                                                    {!codigoRepresentanteValidating && codigoRepresentanteValid === true && (
                                                        <span className="input-success-message" style={{ color: '#28a745', fontSize: '0.85rem' }}>Código válido</span>
                                                    )}
                                                    {!codigoRepresentanteValidating && codigoRepresentanteError && (
                                                        <span className="input-error-message" style={{ color: '#dc3545', fontSize: '0.85rem' }}>{codigoRepresentanteError}</span>
                                                    )}
                                                    {!codigoRepresentanteValidating && codigoRepresentante.length > 0 && codigoRepresentante.length < 5 && (
                                                        <span className="input-info-message" style={{ color: '#6c757d', fontSize: '0.85rem' }}>O código deve conter 5 dígitos</span>
                                                    )}
                                                </div>
                                            )
                                        }

                                        if (question.questionAlias.toLowerCase() === 'question_responsible_document') {

                                            return (
                                                <div className="control" style={{ display: 'none' }}>
                                                    <Input
                                                        id={String(question.id)}
                                                        register={register}
                                                        errors={errors}
                                                        type="text"
                                                        key={question.questionAlias.toLowerCase()}
                                                        name={String(question.id)}
                                                        defaultValue={questionResponsibleDocument}
                                                        placeholder={question.title}
                                                        errorMessage={`Beneficiario menor de idade, obrigatório o CPF do responsável`}
                                                        min={1}
                                                        required={question.isRequired}
                                                        mask="999.999.999-99"
                                                        hidden
                                                    />
                                                </div>
                                            )

                                        }

                                        if (question.questionAlias.toLowerCase() === 'question_responsible_name') {

                                            return (
                                                <div className="control" style={{ display: 'none' }}>
                                                    <Input
                                                        id={String(question.id)}
                                                        type="text"
                                                        register={register}
                                                        errors={errors}
                                                        key={question.questionAlias.toLowerCase()}
                                                        name={String(question.id)}
                                                        defaultValue={questionResponsibleName}
                                                        placeholder={question.title}
                                                        maxLength={question.maximumAnswerLength}
                                                        hidden
                                                    />
                                                </div>
                                            )
                                        }

                                        if (question.questionAlias.toLowerCase() === 'question_applied_dose') {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <Input
                                                        id={String(question.id)}
                                                        register={register}
                                                        errors={errors}
                                                        type="text"
                                                        key={question.questionAlias.toLowerCase()}
                                                        name={String(question.id)}
                                                        placeholder={question.title}
                                                        maxLength={question.maximumAnswerLength}
                                                        errorMessage={`${question.title} obrigatório`}
                                                        min={1}
                                                        required={question.isRequired}
                                                        patt={DOSE_PATTERN(question.title)}
                                                        onKeyPress={handleKeyPressOnlyNumberAndDot}
                                                    />
                                                </div>
                                            )
                                        }

                                        if ((question.questionAlias.toLowerCase() !== 'question_lgpd_data_accepted_term'
                                            && question.questionAlias.toLowerCase() !== 'question_lgpd_ip')) {

                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name={String(question.id)}
                                                        placeholder={question.title}
                                                        maxLength={question.maximumAnswerLength}
                                                        ref={register({
                                                            required: {
                                                                value: question.isRequired,
                                                                message: `${question.title} obrigatório`
                                                            }
                                                        })}
                                                    />
                                                </div>
                                            )
                                        }

                                        break;
                                    case "textolongo":
                                        return (
                                            <div className="control">
                                                <label htmlFor={String(question.id)}>
                                                    {question.title}
                                                </label>
                                                <textarea
                                                    name={String(question.id)}
                                                    cols={10}
                                                    rows={10}
                                                    defaultValue={response?.filter(f => f.id === question.id)[0]?.value}
                                                    placeholder={question.title}
                                                    ref={register({
                                                        required: {
                                                            value: question.isRequired,
                                                            message: `${question.title} de prenchimento obrigatório`
                                                        }
                                                    })}
                                                >
                                                </textarea>

                                            </div>
                                        )
                                    case "radiobutton":
                                        if (!(question.questionAlias.toLowerCase() === 'question_receive_access')) {
                                            return (
                                                <div className="react-modal-form-radio">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <div className="radio-container">
                                                        {question.allowedValues?.map(allowed => (
                                                            <>
                                                                <label key={allowed.value} htmlFor={allowed.label}>
                                                                    {allowed.label}
                                                                </label>
                                                                <input
                                                                    id={allowed.label}
                                                                    type="radio"
                                                                    name={String(question.id)}
                                                                    value={allowed.value}
                                                                    defaultValue={response?.filter(f => f.id === question.id)[0]?.value}
                                                                    ref={register({
                                                                        required: {
                                                                            value: question.isRequired,
                                                                            message: `${question.title} de prenchimento obrigatório`
                                                                        }
                                                                    })}
                                                                />
                                                            </>
                                                        ))}
                                                    </div>
                                                    {errors[String(question.id)] && <span className="input-error-message">{errors[String(question.id)].message}</span>}
                                                </div>
                                            )
                                        }
                                        break;

                                    case "data":
                                        if (!(question.questionAlias.toLowerCase() === 'question_responsible_birth_date')) {
                                            return (
                                                <div className="control">
                                                    <label htmlFor={String(question.id)}>
                                                        {question.title}
                                                    </label>
                                                    <Input
                                                        id={String(question.id)}
                                                        register={register}
                                                        errors={errors}
                                                        type="date"
                                                        key={question.questionAlias.toLowerCase()}
                                                        name={String(1)}
                                                        placeholder={question.title}
                                                        defaultValue={new Intl.DateTimeFormat('fr-ca').format(
                                                            new Date(response?.filter(f => f.id === question.id)[0]?.value?.split('/').reverse().join('-')))}
                                                        errorMessage={`${question.title} de prenchimento obrigatório`}
                                                        required={question.isRequired}
                                                        max={new Intl.DateTimeFormat('fr-ca').format(new Date())}
                                                    />
                                                </div>
                                            );
                                        }
                                }
                            })}

                        </Modal.Body>
                        <Modal.Footer>
                            <button type='button' className='cancelar' onClick={handleCloseModal}>Cancelar</button>
                            <button
                                type="submit"
                                disabled={
                                    codigoRepresentanteValidating ||
                                    (codigoRepresentante.trim().length > 0 && codigoRepresentanteValid !== true)
                                }
                            >
                                Adicionar
                            </button>
                        </Modal.Footer>
                    </Container>
                </Conten>
            </Modal>
        </ModalContainer>
    );
}
